import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Alert,
  FormControlLabel,
  Switch,
  Snackbar,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LoginIcon from "@mui/icons-material/Login";
import SaveIcon from "@mui/icons-material/Save";
import { useGame, useGameDispatch } from "../context/GameContext";
import { getAlbumArt } from "../utils/albumArt";
import { getUserInfo } from "../api/getCommands";
import { saveBracket } from "../api/postCommands";
import SeoHead from "../components/SeoHead";
import AdBanner from "../components/AdBanner";
import { useTranslation } from "react-i18next";

const placeColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function ResultsPage() {
  const { siteData } = useGame();
  const dispatch = useGameDispatch();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const { t } = useTranslation();

  // Check if user is logged in
  useEffect(() => {
    getUserInfo()
      .then((data) => setIsLoggedIn(!!data?.user))
      .catch(() => setIsLoggedIn(false));
  }, []);

  const { gameResultSeeds, gameResults } = siteData.bracketsModeSettings;
  const songs = siteData.gameSessionSongs;

  // Build ranking from results (last entry = winner)
  const rankings: number[] = [];

  if (siteData.gameType === "DoubleE") {
    // Double elimination: gameResults has losers in order, last gameResultSeeds entry is winner
    const allSeeds = gameResultSeeds.flat();
    for (let i = allSeeds.length - 1; i >= 0; i--) {
      const seed = allSeeds[i]!;
      if (!rankings.includes(seed)) rankings.push(seed);
    }
  } else {
    // Single elimination: gameResultSeeds has rounds, last entry is winner
    for (let i = gameResultSeeds.length - 1; i >= 0; i--) {
      const round = gameResultSeeds[i]!;
      const seeds = Array.isArray(round) ? round : [round];
      for (const s of seeds) {
        if (!rankings.includes(s)) rankings.push(s);
      }
    }
    // Add any from gameResults not already in rankings
    for (let i = gameResults.length - 1; i >= 0; i--) {
      if (!rankings.includes(gameResults[i]!)) rankings.push(gameResults[i]!);
    }
  }

  async function handleSave() {
    if (!rankings.length) return;
    setSaving(true);
    const winnerSong = songs[rankings[0]! - 1];
    try {
      const result = await saveBracket({
        gameMode: siteData.gameMode,
        gameType: siteData.gameType,
        bracketSize: siteData.bracketSize,
        artistSelected: siteData.artistSelected,
        winnerSongName: winnerSong?.SongName || "Unknown",
        winnerArtist: winnerSong?.Artist || "",
        bracketData: {
          gameResultSeeds,
          gameResults,
          songs: songs.map((s) => ({
            Id: s.Id,
            SongName: s.SongName,
            Artist: s.Artist,
            AlbumName: s.AlbumName,
            AlbumId: s.AlbumId,
            AlbumArt: s.AlbumArt,
          })),
        },
        isPublic,
      });
      if (result.success) {
        setSaved(true);
        setSaveMessage("Bracket saved!" + (isPublic ? " It's now visible on the Explore page." : ""));
      } else {
        setSaveMessage("Failed to save bracket.");
      }
    } catch {
      setSaveMessage("Failed to save bracket.");
    } finally {
      setSaving(false);
    }
  }

  function handleNewGame() {
    dispatch({ type: "RESET_GAME" });
    navigate("/");
  }

  return (
    <Box id="results-page" sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
      <SeoHead title="Results" description="View your bracket tournament results and final rankings." />
      <Typography id="results-title" variant="h4" textAlign="center" gutterBottom>
        <EmojiEventsIcon sx={{ fontSize: 40, color: "#FFD700", mr: 1, verticalAlign: "middle" }} />
        {t("results.bracketResults")}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3, maxWidth: 550, mx: "auto" }}>
        Your bracket tournament is complete. Below are the final rankings based on every matchup
        you voted on. The top-ranked songs earned their place by winning the most head-to-head
        comparisons throughout the bracket.
      </Typography>

      {!isLoggedIn && (
        <Alert
          id="results-login-alert"
          severity="info"
          icon={<LoginIcon />}
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                // Trigger Google sign-in if available
                const btn = document.getElementById("google-signin-trigger");
                if (btn) btn.click();
              }}
            >
              {t("nav.signIn")}
            </Button>
          }
        >
          Sign in with Google to save your brackets and track your progress!
        </Alert>
      )}

      {rankings.slice(0, 8).map((seed, index) => {
        const song = songs[seed - 1];
        if (!song) return null;
        const place = index + 1;
        const albumArt = getAlbumArt(song.AlbumId) || song.AlbumArt;

        return (
          <Card key={seed} sx={{ mb: 2, display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 60,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: placeColors[index] ?? "grey.800",
                color: index < 3 ? "#000" : "#fff",
                fontWeight: "bold",
                fontSize: "1.5rem",
                alignSelf: "stretch",
              }}
            >
              {place}
            </Box>
            {albumArt && (
              <CardMedia
                component="img"
                image={albumArt}
                alt={song.AlbumName}
                sx={{ width: 80, height: 80, objectFit: "cover" }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (song.AlbumArt && target.src !== song.AlbumArt) {
                    target.src = song.AlbumArt;
                  }
                }}
              />
            )}
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6">{song.SongName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {song.Artist} — {song.AlbumName}
              </Typography>
            </CardContent>
            <Chip label={`Seed ${seed}`} sx={{ mr: 2 }} size="small" />
          </Card>
        );
      })}

      <AdBanner adSlot="1234567891" />

      <Divider sx={{ my: 3 }} />

      {isLoggedIn && !saved && (
        <Box id="results-save-section" sx={{ textAlign: "center", mb: 2 }}>
          <FormControlLabel
            id="results-public-toggle"
            control={
              <Switch
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
            }
            label={t("results.makePublic")}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Share this bracket on the Explore page for others to see
          </Typography>
          <Button
            id="results-save-btn"
            variant="outlined"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ mr: 2 }}
          >
            {saving ? "Saving..." : t("results.saveResults")}
          </Button>
        </Box>
      )}

      <Box id="results-new-game" sx={{ textAlign: "center" }}>
        <Button id="results-play-again-btn" variant="contained" size="large" onClick={handleNewGame}>
          {t("results.playAgain")}
        </Button>
      </Box>

      {/* Results info */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Understanding Your Rankings
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Your final rankings reflect every decision you made throughout the bracket. The champion
          at the top won every matchup on the winners side. Songs ranked 2nd and 3rd lost only to
          higher-ranked songs. In double elimination, the rankings are especially meaningful because
          every song had a second chance — a song had to lose twice to be eliminated.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Want different results? Try playing the same artist again — songs are randomized each
          time, and you might feel differently about certain matchups on a different day. You can
          also explore other game modes from the home page or browse what other players picked on
          the Explore page.
        </Typography>
      </Box>

      <Snackbar
        open={!!saveMessage}
        autoHideDuration={4000}
        onClose={() => setSaveMessage("")}
        message={saveMessage}
      />
    </Box>
  );
}
